import { supabase } from '@foundation/altzero_base/base/utils/supabaseClient';
import html2canvas from 'html2canvas';
import type { EditorStorageConfig } from '../types/editor';

export interface Template {
  id: string;
  name: string;
  description?: string;
  bucket_path: string;
  user_id: string;
  category?: string;
  sections?: any;
  created_at: string;
  updated_at: string;
}

export interface TemplateData {
  name: string;
  description?: string;
  is_public?: boolean;
  editorState: {
    pages: any[];
    currentPageId: string;
    zoom: number;
    showGrid: boolean;
  };
  exportInfo: {
    timestamp: string;
    version: string;
    totalPages: number;
    totalBlocks: number;
    exportType: string;
  };
  metadata: {
    userAgent: string;
    screenResolution: string;
    exportedBy: string;
  };
}

export interface SaveTemplateResult {
  success: boolean;
  template?: Template;
  error?: string;
}

export interface TemplateListResult {
  success: boolean;
  templates?: Template[];
  error?: string;
}

class TemplateStorageService {
  private readonly DEFAULT_BUCKET = 'proposaltemplates';
  private readonly DEFAULT_TABLE = 'scopingai_proposaltemplates';

  private getBucket(cfg?: EditorStorageConfig) { return cfg?.bucketName ?? this.DEFAULT_BUCKET; }
  private getTable(cfg?: EditorStorageConfig) { return cfg?.tableName ?? this.DEFAULT_TABLE; }
  private getStorageClient(cfg?: EditorStorageConfig) { return cfg?.supabaseClient ?? supabase; }
  private getDbClient(cfg?: EditorStorageConfig) { return cfg?.supabaseClient ?? supabase; }

  /**
   * Save template to Supabase storage and database
   */
  async saveTemplate(
    templateData: TemplateData,
    userId: string,
    templateId?: string,
    storageConfig?: EditorStorageConfig
  ): Promise<SaveTemplateResult> {
    try {
      const bucket = this.getBucket(storageConfig);
      const table = this.getTable(storageConfig);
      const storageClient = this.getStorageClient(storageConfig);
      const dbClient = this.getDbClient(storageConfig);

      console.log('💾 Saving template:', templateData.name, `(bucket: ${bucket}, table: ${table})`);

      const timestamp = Date.now();
      const sanitizedName = templateData.name.replace(/[^a-zA-Z0-9\-_\s]/g, '').replace(/\s+/g, '_');
      const fileName = `${sanitizedName}_${timestamp}.json`;
      const filePath = `${userId}/${fileName}`;

      const { data: uploadData, error: uploadError } = await storageClient.storage
        .from(bucket)
        .upload(filePath, JSON.stringify(templateData, null, 2), {
          contentType: 'application/json',
          upsert: false,
        });

      if (uploadError) {
        console.error('❌ Template upload error:', uploadError);
        return { success: false, error: `Upload failed: ${uploadError.message}` };
      }

      console.log('✅ Template uploaded to storage:', uploadData.path);

      let template: Template;
      
      if (templateId) {
        const { data, error } = await dbClient
          .from(table)
          .update({
            name: templateData.name,
            description: templateData.description,
            bucket_path: uploadData.path,
            is_public: templateData.is_public ?? false,
            updated_at: new Date().toISOString(),
          })
          .eq('id', templateId)
          .eq('user_id', userId)
          .select()
          .single();

        if (error) {
          console.error('❌ Database update error:', error);
          return { success: false, error: `Database update failed: ${error.message}` };
        }

        template = data;
      } else {
        const { data, error } = await dbClient
          .from(table)
          .insert({
            name: templateData.name,
            description: templateData.description,
            bucket_path: uploadData.path,
            user_id: userId,
            is_public: templateData.is_public ?? false,
          })
          .select()
          .single();

        if (error) {
          console.error('❌ Database insert error:', error);
          return { success: false, error: `Database insert failed: ${error.message}` };
        }

        template = data;
      }

      console.log('✅ Template saved successfully:', template.id);
      return { success: true, template };

    } catch (error) {
      console.error('❌ Template save error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  /**
   * Update existing template
   */
  async updateTemplate(templateId: string, templateData: TemplateData, userId: string, storageConfig?: EditorStorageConfig): Promise<SaveTemplateResult> {
    try {
      const bucket = this.getBucket(storageConfig);
      const table = this.getTable(storageConfig);
      const storageClient = this.getStorageClient(storageConfig);
      const dbClient = this.getDbClient(storageConfig);

      const { data: existingTemplate, error: fetchError } = await dbClient
        .from(table)
        .select('*')
        .eq('id', templateId)
        .eq('user_id', userId)
        .single();

      if (fetchError || !existingTemplate) {
        return {
          success: false,
          error: 'Template not found or access denied'
        };
      }

      const fileContent = JSON.stringify(templateData, null, 2);
      const { error: uploadError } = await storageClient.storage
        .from(bucket)
        .update(existingTemplate.bucket_path, fileContent, {
          contentType: 'application/json',
          upsert: true
        });

      if (uploadError) {
        return {
          success: false,
          error: `Failed to update template file: ${uploadError.message}`
        };
      }

      const { data: updatedTemplate, error: updateError } = await dbClient
        .from(table)
        .update({
          description: templateData.description,
          is_public: templateData.is_public ?? false,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId)
        .eq('user_id', userId)
        .select()
        .single();

      if (updateError) {
        return {
          success: false,
          error: `Failed to update template metadata: ${updateError.message}`
        };
      }

      return {
        success: true,
        template: updatedTemplate
      };

    } catch (error) {
      console.error('Error updating template:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get all templates for a user
   */
  async getUserTemplates(userId: string, storageConfig?: EditorStorageConfig): Promise<TemplateListResult> {
    try {
      const dbClient = this.getDbClient(storageConfig);
      const table = this.getTable(storageConfig);

      const { data, error } = await dbClient
        .from(table)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching templates:', error);
        return { success: false, error: error.message };
      }

      return { success: true, templates: data || [] };
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  async getAllTemplates(storageConfig?: EditorStorageConfig): Promise<TemplateListResult> {
    try {
      const dbClient = this.getDbClient(storageConfig);
      const table = this.getTable(storageConfig);

      const { data, error } = await dbClient
        .from(table)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching all templates:', error);
        return { success: false, error: error.message };
      }

      return { success: true, templates: data || [] };
    } catch (error) {
      console.error('❌ Error fetching all templates:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  async loadTemplate(template: Template, storageConfig?: EditorStorageConfig): Promise<{ success: boolean; data?: TemplateData; error?: string }> {
    try {
      const storageClient = this.getStorageClient(storageConfig);
      const bucket = this.getBucket(storageConfig);

      const { data, error } = await storageClient.storage
        .from(bucket)
        .download(template.bucket_path);

      if (error) {
        console.error('❌ Template download error:', error);
        return { success: false, error: error.message };
      }

      const text = await data.text();
      const templateData = JSON.parse(text) as TemplateData;

      return { success: true, data: templateData };
    } catch (error) {
      console.error('❌ Template load error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  async deleteTemplate(templateId: string, userId: string, storageConfig?: EditorStorageConfig): Promise<{ success: boolean; error?: string }> {
    try {
      const dbClient = this.getDbClient(storageConfig);
      const table = this.getTable(storageConfig);
      const storageClient = this.getStorageClient(storageConfig);
      const bucket = this.getBucket(storageConfig);

      const { data: template, error: fetchError } = await dbClient
        .from(table)
        .select('bucket_path')
        .eq('id', templateId)
        .eq('user_id', userId)
        .single();

      if (fetchError) {
        return { success: false, error: fetchError.message };
      }

      if (template.bucket_path) {
        const { error: storageError } = await storageClient.storage
          .from(bucket)
          .remove([template.bucket_path]);

        if (storageError) {
          console.warn('⚠️ Storage deletion warning:', storageError);
        }
      }

      const { error: dbError } = await dbClient
        .from(table)
        .delete()
        .eq('id', templateId)
        .eq('user_id', userId);

      if (dbError) {
        return { success: false, error: dbError.message };
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Template deletion error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

}

export const templateStorageService = new TemplateStorageService();
